import { useRedirectFunctions } from '@propelauth/react';

import { useUpdateOwnPracticeArea } from '@/api/mutations/useUpdateOwnPracticeArea';
import { useUserContext } from '@/Contexts/User';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PracticeAreaSelect } from '@/new/components/practice-area-select';
import { SettingsContent, SettingsRow, SettingsSection } from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';

export function UserSettings() {
  const { redirectToAccountPage } = useRedirectFunctions();
  const { isAuthenticated, isLoading, isDisconnecting, disconnectRelativity } = useRelativityAuth();
  const { user } = useUserContext();
  const updatePracticeArea = useUpdateOwnPracticeArea();

  return (
    <>
      <Breadcrumb items={[{ href: '/', label: 'Home', active: true }]} />

      <SettingsContent className="mt-6">
        <SettingsSection title="User Settings" description="Manage your account details.">
          <SettingsRow title="Practice area" description="Choose your primary practice area.">
            <PracticeAreaSelect
              value={user?.practiceArea ?? null}
              onValueChange={(practiceArea) => updatePracticeArea.mutate(practiceArea)}
              disabled={updatePracticeArea.isLoading}
              className="w-64"
            />
          </SettingsRow>

          <SettingsRow title="Account" description="Manage your password, email, and other account security settings.">
            <Button
              type="button"
              variant="ghost"
              className="text-blue-500 hover:text-blue-600"
              onClick={() => redirectToAccountPage()}
            >
              Manage Account
              <Icon className="lucide--arrow-up-right" />
            </Button>
          </SettingsRow>
        </SettingsSection>

        <SettingsSection title="Connections" description="Manage your connections to third-party services.">
          <SettingsRow
            title="Relativity"
            description={
              isLoading
                ? 'Checking your Relativity connection...'
                : isAuthenticated
                  ? 'Wexler is using your current Relativity session.'
                  : 'You are not currently connected. You will be prompted to authenticate when you next use a Relativity workflow.'
            }
          >
            {isLoading ? (
              <Icon className="lucide--loader-2 size-4 animate-spin text-gray-500" />
            ) : isAuthenticated ? (
              <Button onClick={disconnectRelativity} disabled={isDisconnecting}>
                {isDisconnecting ? (
                  <>
                    <Icon className="lucide--loader-2 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  'Log out of Relativity'
                )}
              </Button>
            ) : null}
          </SettingsRow>
        </SettingsSection>
      </SettingsContent>
    </>
  );
}
